import { NewProduct, OldProduct, ProductType } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import productQueryKeys from "../query-keys/product.query-keys";
import structureQueryKeys from "../query-keys/structure.query-keys";
import { CreateProductForm, UpdateProductForm } from "../schemas";

const createProduct = async (structureId: string, body: CreateProductForm) => {
  try {
    const { data } = await privateAxios.post<ProductType>(
      `/structures/${structureId}/products`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useCreateProductMutation = (structureId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProductForm) =>
      createProduct(structureId ?? "mine", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureId
          ? productQueryKeys.list(structureId)
          : productQueryKeys.lists(),
      });
    },
  });
};

const updateProduct = async (
  structureId: string,
  productId: string,
  body: UpdateProductForm
) => {
  try {
    const { data } = await privateAxios.patch<ProductType>(
      `/structures/${structureId}/products/${productId}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useUpdateProductMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      body,
    }: {
      productId: string;
      body: UpdateProductForm;
    }) => updateProduct(structureId ?? "mine", productId, body),
    onSuccess: (_, { productId }) => {
      queryClient.invalidateQueries({
        queryKey: productQueryKeys.detail(productId),
      });
      queryClient.invalidateQueries({
        queryKey: structureId
          ? productQueryKeys.list(structureId)
          : productQueryKeys.lists(),
      });
    },
  });
};

async function destroyProduct(structureId: string, productId: string) {
  try {
    await privateAxios.delete(
      `/structures/${structureId}/products/${productId}`
    );
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
}

export const useDeleteProductMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      destroyProduct(structureId ?? "mine", productId),
    onSuccess: (_, productId) => {
      queryClient.invalidateQueries({
        queryKey: productQueryKeys.detail(productId),
      });
      queryClient.invalidateQueries({
        queryKey: structureId
          ? productQueryKeys.list(structureId)
          : productQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.structure(),
      });
    },
  });
};

export type ImportProductBody = {
  add: NewProduct[];
  delete: OldProduct[];
};

async function importProducts(structureId: string, body: ImportProductBody) {
  try {
    await privateAxios.post(`/structures/${structureId}/products/import`, body);
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
}

export const useImportProductsMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ImportProductBody) =>
      importProducts(structureId ?? "mine", body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureId
          ? productQueryKeys.list(structureId)
          : productQueryKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: structureQueryKeys.structure(),
      });
    },
  });
};

const createAndAssignProductToEvent = async (
  structureId: string,
  eventId: string,
  body: CreateProductForm
) => {
  try {
    const { data } = await privateAxios.post<ProductType>(
      `/structures/${structureId}/products/events/${eventId}`,
      body
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useCreateAndAssignProductToEventMutation = (
  structureId?: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      body,
    }: {
      eventId: string;
      body: CreateProductForm;
    }) => createAndAssignProductToEvent(structureId ?? "mine", eventId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: structureId
          ? productQueryKeys.list(structureId)
          : productQueryKeys.lists(),
      });
    },
  });
};

const assignProductsToEvent = async (
  structureId: string,
  eventId: string,
  productIds: string[]
) => {
  try {
    await privateAxios.post(
      `/structures/${structureId}/products/events/${eventId}/assign`,
      {
        productIds,
      }
    );
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useAssignProductsToEventMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      productIds,
    }: {
      eventId: string;
      productIds: string[];
    }) => assignProductsToEvent(structureId ?? "mine", eventId, productIds),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: structureId
          ? productQueryKeys.eventList(structureId, eventId)
          : productQueryKeys.lists(),
      });
    },
  });
};

const unassignProductToEvent = async (
  structureId: string,
  eventId: string,
  productId: string
) => {
  try {
    await privateAxios.delete(
      `/structures/${structureId}/products/events/${eventId}/${productId}`
    );
  } catch (error) {
    if (isAxiosError(error)) {
      throw new Error(error.response?.data.message || error.message);
    }
    throw error;
  }
};

export const useUnassignProductFromEventMutation = (structureId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      productId,
    }: {
      eventId: string;
      productId: string;
    }) => unassignProductToEvent(structureId ?? "mine", eventId, productId),
    onSuccess: (_, { eventId }) => {
      queryClient.invalidateQueries({
        queryKey: structureId
          ? productQueryKeys.eventList(structureId, eventId)
          : productQueryKeys.lists(),
      });
    },
  });
};
